import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FileButton,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconBriefcase,
  IconCalendar,
  IconClipboardPlus,
  IconDownload,
  IconEdit,
  IconId,
  IconMail,
  IconMapPin,
  IconPhone,
  IconQrcode,
  IconUpload,
  IconUser,
  IconUserCircle,
} from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { OpenExamDialog } from "@/features/examinations/OpenExamDialog";
import { GenerateCardDialog } from "@/features/patients/GenerateCardDialog";
import { PatientEditDialog } from "@/features/patients/PatientEditDialog";
import { api } from "@/lib/api/client";
import { getPatient, uploadPhoto } from "@/lib/api/patients";
import { useAuthStore } from "@/stores/auth";

export function PatientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canUpdate = useAuthStore((s) => s.hasPermission("patient.update"));
  const canOpenExam = useAuthStore((s) => s.hasPermission("examination.create"));
  const [examDialogOpen, examDialogHandlers] = useDisclosure(false);
  const [cardDialogOpen, cardDialogHandlers] = useDisclosure(false);
  const [editDialogOpen, editDialogHandlers] = useDisclosure(false);

  const { data, isLoading } = useQuery({
    queryKey: ["patient", id],
    queryFn: () => getPatient(id!),
    enabled: !!id,
  });


  const qrQuery = useQuery({
    queryKey: ["patient-qr", id],
    queryFn: async () => {
      const response = await api.get(`/patients/${id}/card-qr`, { responseType: "blob" });
      return URL.createObjectURL(response.data as Blob);
    },
    enabled: !!id,
  });

  if (isLoading) return <Loader />;
  if (!data) return <Text c="dimmed">{t("patients.not_found")}</Text>;

  const fullName = [data.family_name, data.given_name, data.father_name]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <PageHeader
        title={fullName}
        subtitle={`${t("patients.years_old", { age: data.age })} · ${data.card_number}`}
        back
        actions={
          <>
            {canOpenExam && (
              <Button
                leftSection={<IconClipboardPlus size={16} strokeWidth={1.8} />}
                onClick={examDialogHandlers.open}
              >
                {t("examinations.open_action")}
              </Button>
            )}
            <Button
              variant="light"
              leftSection={<IconDownload size={16} strokeWidth={1.8} />}
              onClick={cardDialogHandlers.open}
            >
              {t("patients.download_card")}
            </Button>
            {canUpdate && (
              <Button
                variant="light"
                color="orange"
                leftSection={<IconEdit size={16} strokeWidth={1.8} />}
                onClick={editDialogHandlers.open}
              >
                {t("patients.edit", { defaultValue: "Tahrirlash" })}
              </Button>
            )}
            <Button variant="subtle" color="gray" onClick={() => navigate("/patients")}>
              {t("common.back")}
            </Button>
          </>
        }
      />

      <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
        <Card padding="lg" style={{ gridColumn: "span 1" }}>
          <Stack gap="md" align="center">
            <Stack gap={2} align="center">
              <Title order={3} ta="center" fw={700}>
                {fullName}
              </Title>
              <Group gap="xs" mt={4}>
                <Badge
                  size="md"
                  variant="light"
                  color={data.sex === "female" ? "pink" : "blue"}
                  radius="sm"
                >
                  {t(`patients.sex.${data.sex}`)}
                </Badge>
                <Badge size="md" variant="light" color="gray" radius="sm">
                  {data.birth_date}
                </Badge>
              </Group>
            </Stack>

            <Divider w="100%" my="xs" />

            <Stack gap="xs" w="100%">
              <Field
                icon={<IconId size={16} strokeWidth={1.8} />}
                label={t("patients.fields.card")}
                value={data.card_number}
                mono
              />
              {data.jshshir && (
                <Field
                  icon={<IconUserCircle size={16} strokeWidth={1.8} />}
                  label={t("patients.fields.jshshir")}
                  value={data.jshshir}
                  mono
                />
              )}
              <Field
                icon={<IconPhone size={16} strokeWidth={1.8} />}
                label={t("patients.fields.phone")}
                value={data.phone_primary}
                mono
              />
              {data.email && (
                <Field
                  icon={<IconMail size={16} strokeWidth={1.8} />}
                  label={t("patients.fields.email")}
                  value={data.email}
                />
              )}
            </Stack>
          </Stack>
        </Card>

        <Stack gap="lg" style={{ gridColumn: "span 2" }}>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Card padding="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                  <IconCalendar size={18} strokeWidth={1.8} />
                </ThemeIcon>
                <Title order={5} fw={700}>
                  {t("patients.section.identity")}
                </Title>
              </Group>
              <Stack gap="xs">
                <Field
                  label={t("patients.fields.birth_date")}
                  value={`${data.birth_date} (${t("patients.years_old", { age: data.age })})`}
                />
                <Field label={t("patients.fields.sex")} value={t(`patients.sex.${data.sex}`)} />
                {data.passport_seriya && data.passport_number && (
                  <Field
                    label={t("patients.fields.passport")}
                    value={`${data.passport_seriya} ${data.passport_number}`}
                    mono
                  />
                )}
              </Stack>
            </Card>

            <Card padding="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                  <IconMapPin size={18} strokeWidth={1.8} />
                </ThemeIcon>
                <Title order={5} fw={700}>
                  {t("patients.section.address")}
                </Title>
              </Group>
              <Stack gap="xs">
                <Field label={t("patients.fields.region")} value={data.address_region ?? "—"} />
                <Field
                  label={t("patients.fields.district")}
                  value={data.address_district ?? "—"}
                />
                <Field
                  label={t("patients.fields.address_full")}
                  value={data.address_full ?? "—"}
                />
              </Stack>
            </Card>

            <Card padding="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                  <IconBriefcase size={18} strokeWidth={1.8} />
                </ThemeIcon>
                <Title order={5} fw={700}>
                  {t("patients.section.workplace")}
                </Title>
              </Group>
              <Stack gap="xs">
                <Field label={t("patients.fields.workplace")} value={data.workplace ?? "—"} />
                <Field label={t("patients.fields.position")} value={data.position ?? "—"} />
              </Stack>
            </Card>

            <Card padding="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                  <IconQrcode size={18} strokeWidth={1.8} />
                </ThemeIcon>
                <Title order={5} fw={700}>
                  {t("patients.section.card")}
                </Title>
              </Group>
              <Stack gap="xs" align="center">
                {qrQuery.data && (
                  <Box
                    p="sm"
                    style={{
                      background: "white",
                      borderRadius: 12,
                      border: "1px solid var(--mantine-color-gray-2)",
                    }}
                  >
                    <Image src={qrQuery.data} w={140} h={140} alt="QR" />
                  </Box>
                )}
                <Stack gap={2} align="center" w="100%" mt="xs">
                  <Text ff="monospace" size="sm" fw={700}>
                    {data.card_number}
                  </Text>
                  <Text c="dimmed" size="xs" ff="monospace">
                    {data.card_barcode}
                  </Text>
                </Stack>
              </Stack>
            </Card>
          </SimpleGrid>
        </Stack>
      </SimpleGrid>

      {data && (
        <OpenExamDialog
          patientId={data.id}
          opened={examDialogOpen}
          onClose={examDialogHandlers.close}
        />
      )}
      {data && (
        <GenerateCardDialog
          patientId={data.id}
          cardNumber={data.card_number}
          opened={cardDialogOpen}
          onClose={cardDialogHandlers.close}
        />
      )}
      {data && (
        <PatientEditDialog
          patient={data}
          opened={editDialogOpen}
          onClose={editDialogHandlers.close}
        />
      )}
    </>
  );
}

function Field({
  label,
  value,
  mono,
  icon,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  icon?: ReactNode;
}) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
      <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
        {icon && <Box c="dimmed">{icon}</Box>}
        <Text c="dimmed" size="sm" fw={500}>
          {label}
        </Text>
      </Group>
      {value ? (
        <Text size="sm" ff={mono ? "monospace" : undefined} ta="right" fw={500}>
          {value}
        </Text>
      ) : (
        <Text size="sm" c="dimmed">
          —
        </Text>
      )}
    </Group>
  );
}
